import { MouseEvent, useMemo } from 'react'
import { t } from 'i18next'
import { v4 as uuid } from 'uuid'

import { OpenFormProps } from '@pages/ConfiguredEntity/types'

import { useFetchParameterPropertiesQuery } from '@redux/api/parameters.api'
import { openFormDialog } from '@redux/reducers/dialogWindowManager.reducer'
import { addFormHistory } from '@redux/reducers/formHistory.reducer'
import { showMessage } from '@redux/reducers/snackbar.reducer'

import { useAppDispatch } from '@hooks'
import {
  getDialogWindowsFromReduxStore,
  getInitialPositionWindow,
  transformCommonParametersToValues,
} from '@helpers'
import { ACTION_CODE, COMMON_PARAMETERS } from '@constants'
import { ActionResultType, ActionRow, DIALOG_WINDOW_TYPE, ObjectDataValue } from '@types'

import { MuiEvent } from '../../../../wiskey-react-table-component/src'

export const useOpenForm = () => {
  const dispatch = useAppDispatch()
  const { data: commonParameters } = useFetchParameterPropertiesQuery({
    code: COMMON_PARAMETERS.STATE,
  })

  const initialCommonParameters = useMemo(
    () => transformCommonParametersToValues(commonParameters),
    [commonParameters]
  )

  const openForm = (
    action: ActionRow,
    srcObj: {
      formProps?: OpenFormProps
      event?: MouseEvent<HTMLButtonElement> | MuiEvent<MouseEvent>
      additionalProps?: {
        addHistory?: boolean
      }
    } = {}
  ) => {
    const { formProps, event, additionalProps } = srcObj

    if (!formProps || !event) {
      return Promise.resolve({
        actionCode: ACTION_CODE.OPEN_FORM_MODEL,
        value: {
          opened: false,
          srcObj,
        },
      } as ActionResultType<Record<string, ObjectDataValue>>)
    }

    const { id } = formProps

    const dialogWindows = getDialogWindowsFromReduxStore()

    const openedFormsCount = dialogWindows.filter(
      dialog =>
        [DIALOG_WINDOW_TYPE.VIEW, DIALOG_WINDOW_TYPE.FORM].includes(dialog.type) && !dialog.hidden
    ).length

    const maxOpenedFormsCount = Number(initialCommonParameters.numberOfWindows)

    if (openedFormsCount === maxOpenedFormsCount) {
      dispatch(
        showMessage({
          type: 'info',
          text: t('error.dynamicFormsExceeded', { count: maxOpenedFormsCount }),
        })
      )

      return
    }

    const globalId = uuid()

    const initialPosition = getInitialPositionWindow(event)

    if (additionalProps?.addHistory) {
      dispatch(
        addFormHistory({
          id: id || globalId,
          form: {
            ...formProps,
            isWindow: true,
            title: action.objectFormCode as string,
            objectId: formProps?.objectId || null,
            id: id || globalId,
            formCode: action.objectFormCode || '',
          },
        })
      )
    }

    dispatch(
      openFormDialog({
        id: id || globalId,
        parentDialogId: null,
        type: DIALOG_WINDOW_TYPE.FORM,
        title: null,
        meta: {
          ...formProps,
          globalId: id || globalId,
          formCode: action.objectFormCode || '',
        },
        initialPosition,
      })
    )

    return Promise.resolve({
      actionCode: ACTION_CODE.OPEN_FORM_MODEL,
      value: {
        opened: true,
        srcObj,
      },
    } as ActionResultType<Record<string, ObjectDataValue>>)
  }

  return {
    openForm,
  }
}
